import {
  useTheme,
  Box,
  Typography,
  Button,
  Collapse,
  Skeleton,
  Fade,
} from "@mui/material";
import { tokens } from "../../theme";
import { useState, useEffect } from "react";
import Header from "../../components/Header";
import Inventory2IconOutlined from "@mui/icons-material/Inventory2Outlined";
// import MonitorHeartOutlined from "@mui/icons-material/MonitorHeartOutlined";
import ReportOutlined from "@mui/icons-material/ReportOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";

import { getInventorySummary } from "../../services/ProductService";

// Product-related imports
import {
  listProducts,
  getProduct,
  updateProduct,
  deleteAllProducts,
  deleteProduct,
} from "../../services/ProductService";
import ProductForm from "../../components/Product/ProductForm";
import ProductDeleteDialog from "../../components/Product/ProductDeleteDialog";
import { DataGrid } from "@mui/x-data-grid";
import { isAdmin } from "../../services/UserService";
import { useLocation } from "react-router-dom";
import CustomToolbar from "../../components/CustomToolbar";
import ActionMenu from "../../components/ActionMenu";
import RestockModal from "../../components/Product/RestockModal";
import ProductHistoryModal from "../../components/Product/ProductHistoryModal";
import SetThresholdModal from "../../components/Product/SetThresholdModal";
import CustomNoRowsOverlay from "../../components/CustomNoRowsOverlay";

const Inventory = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Dashboard Stats States
  const [productCount, setProductCount] = useState(0);
  const [totalStockValue, setTotalStockValue] = useState(0);
  const [inStockCount, setInStockCount] = useState(0);
  const [outOfStockCount, setOutOfStockCount] = useState(0);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);

  // Product Data States
  const [rawData, setRawData] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateRangeFilter, setDateRangeFilter] = useState("");

  // Modals state
  const [open, setOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteProductItem, setDeleteProductItem] = useState(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  const [restockOpen, setRestockOpen] = useState(false);
  const [restockTarget, setRestockTarget] = useState(null);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null);

  const [thresholdOpen, setThresholdOpen] = useState(false);
  const [thresholdTarget, setThresholdTarget] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const location = useLocation();
  const [highlightedRowId, setHighlightedRowId] = useState(null);
  const [fadeOut, setFadeOut] = useState(false);

  const admin = isAdmin();

  // Helper to enforce correct data types and sort desc by id
  const processData = (data) => {
    return data
      .map((item) => ({
        ...item,
        id: item.id,
        quantity: Number(item.quantity) || 0,
        cost: Number(item.cost) || 0,
        totalValue: Number(item.totalValue) || 0,
        lastUpdated: item.lastUpdated ? new Date(item.lastUpdated) : null,
      }))
      .sort((a, b) => b.id - a.id);
  };

  // Fetch Inventory Summary
  useEffect(() => {
    const fetchInventorySummary = async () => {
      setIsSummaryLoading(true);
      try {
        const { data } = await getInventorySummary();
        setProductCount(data.productCount);
        setTotalStockValue(data.stockValue);
        setInStockCount(data.inStockCount);
        setOutOfStockCount(data.outOfStockCount);
        setLowStockCount(data.lowStockCount);
      } catch (err) {
        console.error("Failed to load inventory overview", err);
      } finally {
        setIsSummaryLoading(false);
      }
    };
    fetchInventorySummary();
  }, [rawData]); // Re-fetch summary when data changes

  // Fetch Products Data
  useEffect(() => {
    setIsLoading(true);
    listProducts()
      .then((response) => {
        setRawData(processData(response.data));
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
        setIsLoading(false);
      });
  }, []);

  // Filter Logic with simulated loading delay
  useEffect(() => {
    if (!rawData) return;

    setIsLoading(true);
    setRows([]);

    const timer = setTimeout(() => {
      let filtered = [...rawData];

      // Search query filter
      if (searchQuery) {
        const lowerCaseQuery = searchQuery.toLowerCase();
        filtered = filtered.filter(
          (item) =>
            item.productName?.toLowerCase().includes(lowerCaseQuery) ||
            item.category?.toLowerCase().includes(lowerCaseQuery) ||
            item.supplierName?.toLowerCase().includes(lowerCaseQuery) ||
            item.enteredBy?.toLowerCase().includes(lowerCaseQuery) ||
            item.id?.toString().includes(lowerCaseQuery),
        );
      }

      // Category filter
      if (categoryFilter) {
        filtered = filtered.filter((item) => item.category === categoryFilter);
      }

      // Status filter
      if (statusFilter && statusFilter !== "all") {
        if (statusFilter === "healthy") {
          filtered = filtered.filter((item) => item.quantity > 10);
        } else if (statusFilter === "low") {
          filtered = filtered.filter(
            (item) => item.quantity > 0 && item.quantity <= 10,
          );
        } else if (statusFilter === "out") {
          filtered = filtered.filter((item) => item.quantity === 0);
        }
      }

      // Date filter
      if (dateRangeFilter && dateRangeFilter !== "all") {
        const now = new Date();
        let cutoff = new Date(now);
        if (dateRangeFilter === "24h") cutoff.setHours(now.getHours() - 24);
        else if (dateRangeFilter === "7d") cutoff.setDate(now.getDate() - 7);
        else if (dateRangeFilter === "30d") cutoff.setDate(now.getDate() - 30);

        filtered = filtered.filter((item) => {
          if (!item.lastUpdated) return true; // Keep items without dates intact
          return new Date(item.lastUpdated) >= cutoff;
        });
      }

      setRows(filtered);
      setIsLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, [rawData, searchQuery, categoryFilter, statusFilter, dateRangeFilter]);

  // Routing Highlights
  useEffect(() => {
    if (location.state?.id) {
      const targetId = location.state.id;
      setHighlightedRowId(targetId);
      setTimeout(() => {
        document.querySelector(`[data-id="${targetId}"]`)?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 500);
    }
  }, [location]);

  useEffect(() => {
    if (highlightedRowId) {
      const fadeTimeout = setTimeout(() => {
        setFadeOut(true);
        setTimeout(() => {
          setHighlightedRowId(null);
          setFadeOut(false);
        }, 300);
      }, 2500);
      return () => clearTimeout(fadeTimeout);
    }
  }, [highlightedRowId]);

  const getRowClassName = (params) =>
    params.id === highlightedRowId
      ? fadeOut
        ? "highlighted-row fade-out"
        : "highlighted-row"
      : "";

  // Modals Methods
  const handleClickOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    setEditProduct(null);
  };

  const handleOpenDialog = (id = null) => {
    setDeleteProductItem(id);
    setIsBulkDelete(id === null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handleOpenRestock = (row) => {
    setRestockTarget(row || null);
    setRestockOpen(true);
  };

  const handleCloseRestock = () => {
    setRestockOpen(false);
    setRestockTarget(null);
  };

  const handleOpenHistory = (row) => {
    setHistoryTarget(row || null);
    setHistoryOpen(true);
  };

  const handleCloseHistory = () => {
    setHistoryOpen(false);
    setHistoryTarget(null);
  };

  const handleOpenThreshold = (row) => {
    setThresholdTarget(row || null);
    setThresholdOpen(true);
  };

  const handleCloseThreshold = () => {
    setThresholdOpen(false);
    setThresholdTarget(null);
  };

  // Crud Ops
  const handleAddProduct = (newProduct) =>
    setRawData((prevRows) => processData([...prevRows, newProduct]));

  const handleEditProduct = (row) => {
    getProduct(row.id)
      .then((response) => {
        setEditProduct(response.data);
        setOpen(true);
      })
      .catch((error) => console.error("Error fetching product: ", error));
  };

  const handleProductEdited = (updatedProduct) => {
    updateProduct(updatedProduct.id, updatedProduct)
      .then(() =>
        listProducts().then((response) =>
          setRawData(processData(response.data)),
        ),
      )
      .catch((error) => console.error("Error updating product: ", error));
  };

  const handleDeleteProduct = () => {
    if (isBulkDelete) {
      return deleteAllProducts()
        .then(() => setRawData([]))
        .catch((error) => {
          console.error("Error deleting all products: ", error);
          throw error;
        });
    } else if (deleteProductItem) {
      return deleteProduct(deleteProductItem)
        .then(() =>
          listProducts().then((response) =>
            setRawData(processData(response.data)),
          ),
        )
        .then(() => {
          setDeleteProductItem(null);
        })
        .catch((error) => {
          console.error("Error deleting product: ", error);
          throw error;
        });
    }
    return Promise.resolve();
  };

  const handleOnRestocked = (updatedProduct) => {
    if (!updatedProduct) return handleCloseRestock();
    setRawData((prevRows) => {
      const newData = prevRows.some((r) => r.id === updatedProduct.id)
        ? prevRows.map((r) =>
            r.id === updatedProduct.id ? { ...r, ...updatedProduct } : r,
          )
        : [...prevRows, updatedProduct];
      return processData(newData);
    });
    handleCloseRestock();
  };

  const handleOnThresholdUpdated = (updatedProduct) => {
    if (!updatedProduct) return handleCloseThreshold();
    setRawData((prevRows) => {
      const newData = prevRows.some((r) => r.id === updatedProduct.id)
        ? prevRows.map((r) =>
            r.id === updatedProduct.id ? { ...r, ...updatedProduct } : r,
          )
        : [...prevRows, updatedProduct];
      return processData(newData);
    });
    handleCloseThreshold();
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.4,
      minWidth: 100,
    },
    {
      field: "productName",
      headerName: "Name",
      flex: 0.8,
      minWidth: 110,
    },
    {
      field: "productDesc",
      headerName: "Description",
      flex: 0.9,
      minWidth: 120,
    },
    {
      field: "category",
      headerName: "Category",
      flex: 0.6,
      minWidth: 100,
      type: "string",
    },
    {
      field: "quantity",
      headerName: "Quantity",
      flex: 0.6,
      minWidth: 90,
      type: "number",
      align: "left",
      headerAlign: "left",
    },
    {
      field: "storeLocation",
      headerName: "Location",
      flex: 0.6,
      minWidth: 100,
    },
    {
      field: "cost",
      headerName: "Unit Cost",
      flex: 0.6,
      minWidth: 100,
      type: "number",
      align: "left",
      headerAlign: "left",
      valueFormatter: (params) =>
        new Intl.NumberFormat("en-NG", {
          style: "currency",
          currency: "NGN",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(params),
    },
    {
      field: "totalValue",
      headerName: "Total Value",
      flex: 0.6,
      minWidth: 100,
      type: "number",
      align: "left",
      headerAlign: "left",
      valueFormatter: (params) =>
        new Intl.NumberFormat("en-NG", {
          style: "currency",
          currency: "NGN",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(params),
    },
    {
      field: "supplierName",
      headerName: "Supplier",
      flex: 0.6,
      minWidth: 100,
    },
    {
      field: "enteredBy",
      headerName: "Entered By",
      flex: 0.6,
      minWidth: 100,
    },
    {
      field: "lastUpdated",
      headerName: "Last Updated",
      flex: 0.7,
      minWidth: 120,
      type: "date",
      valueFormatter: (params) => {
        if (!params) return "";
        const date = new Date(params);
        if (isNaN(date.getTime())) return params;

        // Format to DD-MM-YYYY
        const dd = String(date.getDate()).padStart(2, "0");
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const yyyy = date.getFullYear();

        // Format Time to 12-hour AM/PM
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'
        const strTime = `${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;

        return `${dd}-${mm}-${yyyy} ${strTime}`;
      },
    },
    admin
      ? {
          field: "actions",
          headerName: "Actions",
          flex: 0.5,
          minWidth: 80,
          align: "center",
          headerAlign: "center",
          renderCell: (params) => (
            <ActionMenu
              params={params}
              colors={colors}
              handleEditProduct={handleEditProduct}
              handleOpenDialog={handleOpenDialog}
              handleOpenRestock={handleOpenRestock}
              handleOpenHistory={handleOpenHistory}
              handleOpenThreshold={handleOpenThreshold}
            />
          ),
        }
      : {
          field: "actions",
          headerName: "Actions",
          flex: 0.5,
          minWidth: 90,
          align: "center",
          headerAlign: "center",
          renderCell: (params) => (
            <Button
              variant="contained"
              size="small"
              onClick={() => handleOpenRestock(params.row)}
              sx={{
                backgroundColor: colors.blueAccent[500],
                "&:hover": { backgroundColor: colors.blueAccent[600] },
                textTransform: "none",
              }}
            >
              Restock
            </Button>
          ),
        },
  ];

  return (
    <Box
      m={{ xs: "10px", sm: "15px", md: "20px" }}
      sx={{
        "@keyframes slideInUp": {
          "0%": { opacity: 0, transform: "translateY(20px)" },
          "100%": { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        flexWrap={{ xs: "wrap", md: "nowrap" }}
        gap="15px"
        mb={{ xs: "20px", md: "40px" }}
        sx={{
          animation: "slideInUp 0.5s ease-out forwards",
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          sx={{
            flexShrink: 1,
            minWidth: 0,
            width: { xs: "100%", md: "auto" },
            "& > div": { mb: "0 !important" },
            "& h2": {
              fontSize: { xs: "1.5rem", md: "1.5rem", lg: "2rem" },
              mb: "4px !important",
              mt: "0 !important",
              lineHeight: "1 !important",
            },
            "& h5": {
              fontSize: { xs: "0.75rem", md: "0.75rem", lg: "0.875rem" },
              mt: "0 !important",
              mb: "0 !important",
              lineHeight: "1 !important",
            },
          }}
        >
          <Header
            title="INVENTORY"
            subtitle="Track your inventory level here"
          />
        </Box>

        {/* Mobile Filter Toggle */}
        <Box display={{ xs: "block", md: "none" }} width="100%">
          <Button
            fullWidth
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            variant="outlined"
            startIcon={<TuneIcon />}
            sx={{
              justifyContent: "center",
              py: 1,
              mb: 1,
              borderColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.15)"
                  : "rgba(0,0,0,0.15)",
              color: colors.gray[100],
              "&:hover": {
                borderColor: colors.blueAccent[500],
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0,0,0,0.02)",
              },
            }}
          >
            {mobileMenuOpen ? "Hide Actions" : "Actions"}
          </Button>

          <Collapse in={mobileMenuOpen}>
            <Box
              display="flex"
              flexDirection="column"
              gap="10px"
              mb={2}
              p={2}
              sx={{
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.02)"
                    : "#f8fafc",
                borderRadius: "8px",
                border: `1px solid ${
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.05)"
                }`,
              }}
            >
              <Button
                fullWidth
                variant="contained"
                onClick={handleClickOpen}
                startIcon={<AddOutlinedIcon />}
                sx={{
                  backgroundColor: colors.blueAccent[700],
                  color: "#ffffff",
                  py: 1,
                  "&:hover": { backgroundColor: colors.blueAccent[800] },
                }}
              >
                Add Product
              </Button>

              {admin && (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => handleOpenDialog(null)}
                  startIcon={<DeleteOutlinedIcon />}
                  sx={{
                    borderColor: colors.redAccent[500],
                    color: colors.redAccent[500],
                    py: 1,
                    "&:hover": {
                      backgroundColor: colors.redAccent[600],
                      color: "#ffffff",
                    },
                  }}
                >
                  Delete All
                </Button>
              )}
            </Box>
          </Collapse>
        </Box>

        {/* Desktop/Tablet Controls */}
        <Box
          display={{ xs: "none", md: "flex" }}
          flexDirection="row"
          flexWrap="nowrap"
          alignItems="center"
          justifyContent="flex-end"
          width="auto"
          gap={{ md: "8px", lg: "15px" }}
          sx={{ ml: "auto", flexShrink: 0 }}
        >
          {admin && (
            <Button
              sx={{
                height: { md: "38px", lg: "42px" },
                width: "auto",
                minWidth: { md: "auto", lg: 150 },
                backgroundColor: colors.redAccent[600],
                color: "#ffffff",
                fontSize: { md: "0.8rem", lg: "0.875rem" },
                fontWeight: "bold",
                padding: { md: "0 12px", lg: "0 20px" },
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: colors.redAccent[700],
                  transform: "translateY(-1px)",
                },
                "&:active": { transform: "scale(0.95)" },
              }}
              onClick={() => handleOpenDialog(null)}
            >
              <DeleteOutlinedIcon
                sx={{
                  mr: { md: "4px", lg: "8px" },
                  fontSize: { md: "1.125rem", lg: "1.375rem" },
                }}
              />
              Delete All
            </Button>
          )}

          <Button
            sx={{
              height: { md: "38px", lg: "42px" },
              width: "auto",
              minWidth: { md: "auto", lg: 150 },
              backgroundColor: colors.blueAccent[700],
              color: "#ffffff",
              fontSize: { md: "0.8rem", lg: "0.875rem" },
              fontWeight: "bold",
              padding: { md: "0 12px", lg: "0 20px" },
              boxShadow:
                theme.palette.mode === "dark"
                  ? "none"
                  : "0 2px 5px rgba(79, 70, 229, 0.2)",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: colors.blueAccent[800],
                transform: "translateY(-1px)",
              },
              "&:active": { transform: "scale(0.95)" },
            }}
            onClick={handleClickOpen}
          >
            <AddOutlinedIcon
              sx={{
                mr: { md: "4px", lg: "8px" },
                fontSize: { md: "1.125rem", lg: "1.375rem" },
              }}
            />
            Add Product
          </Button>
        </Box>
      </Box>

      {/* Stats Cards - Split into Single Metrics */}
      <Box
        display="grid"
        gridTemplateColumns={{
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          md: "repeat(6, 1fr)",
          lg: "repeat(5, 1fr)",
        }}
        gap="20px"
        mb={{ xs: "25px", md: "40px" }}
        sx={{
          opacity: 0,
          animation: "slideInUp 0.5s ease-out forwards",
          animationDelay: "0.15s",
        }}
      >
        <Box
          p={{ xs: "16px 20px", sm: "20px 24px" }}
          sx={{
            gridColumn: { md: "span 2", lg: "span 1" },
            backgroundColor:
              theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
            borderRadius: "12px",
            border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 4px 20px rgba(0,0,0,0.2)"
                : "0 4px 20px rgba(0,0,0,0.03)",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "10px",
          }}
        >
          <Box display="flex" alignItems="center" gap="10px">
            <Box sx={{ color: colors.blueAccent[500] }}>
              <Inventory2IconOutlined sx={{ fontSize: "1.5rem" }} />
            </Box>
            <Typography
              variant="h6"
              color={colors.gray[400]}
              fontWeight="600"
              textTransform="uppercase"
              letterSpacing="1px"
              sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.875rem" } }}
            >
              Total SKUs
            </Typography>
          </Box>

          {isSummaryLoading ? (
            <Skeleton
              variant="rounded"
              width="80%"
              height={32}
              animation="wave"
              sx={{
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.06)",
                mt: "auto",
              }}
            />
          ) : (
            <Typography
              variant="h3"
              sx={{
                fontWeight: "800",
                color: colors.blueAccent[500],
                fontSize: { xs: "1.25rem", sm: "1.3rem", md: "1.5rem" },
                mt: "auto",
              }}
            >
              {productCount.toLocaleString()}
            </Typography>
          )}
        </Box>

        <Box
          p={{ xs: "16px 20px", sm: "20px 24px" }}
          sx={{
            gridColumn: { md: "span 2", lg: "span 1" },
            backgroundColor:
              theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
            borderRadius: "12px",
            border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 4px 20px rgba(0,0,0,0.2)"
                : "0 4px 20px rgba(0,0,0,0.03)",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "10px",
          }}
        >
          <Box display="flex" alignItems="center" gap="10px">
            <Box sx={{ color: colors.blueAccent[500] }}>
              <AttachMoneyOutlinedIcon sx={{ fontSize: "1.5rem" }} />
            </Box>
            <Typography
              variant="h6"
              color={colors.gray[400]}
              fontWeight="600"
              textTransform="uppercase"
              letterSpacing="1px"
              sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.875rem" } }}
            >
              Stock Value
            </Typography>
          </Box>

          {isSummaryLoading ? (
            <Skeleton
              variant="rounded"
              width="90%"
              height={32}
              animation="wave"
              sx={{
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.06)",
                mt: "auto",
              }}
            />
          ) : (
            <Typography
              variant="h3"
              sx={{
                fontWeight: "800",
                color: colors.blueAccent[500],
                fontSize: { xs: "1.25rem", sm: "1.3rem", md: "1.5rem" },
                mt: "auto",
              }}
            >
              {new Intl.NumberFormat("en-NG", {
                style: "currency",
                currency: "NGN",
                maximumFractionDigits: 0,
              }).format(totalStockValue)}
            </Typography>
          )}
        </Box>

        <Box
          p={{ xs: "16px 20px", sm: "20px 24px" }}
          sx={{
            gridColumn: { md: "span 2", lg: "span 1" },
            backgroundColor:
              theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
            borderRadius: "12px",
            border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 4px 20px rgba(0,0,0,0.2)"
                : "0 4px 20px rgba(0,0,0,0.03)",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "10px",
          }}
        >
          <Box display="flex" alignItems="center" gap="10px">
            <Box sx={{ color: colors.greenAccent[500] }}>
              <CheckCircleOutlineOutlinedIcon sx={{ fontSize: "1.5rem" }} />
            </Box>
            <Typography
              variant="h6"
              color={colors.gray[400]}
              fontWeight="600"
              textTransform="uppercase"
              letterSpacing="1px"
              sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.875rem" } }}
            >
              In-Stock
            </Typography>
          </Box>
          {isSummaryLoading ? (
            <Skeleton
              variant="rounded"
              width="60%"
              height={32}
              animation="wave"
              sx={{
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.06)",
                mt: "auto",
              }}
            />
          ) : (
            <Typography
              variant="h3"
              sx={{
                fontWeight: "800",
                color: colors.greenAccent[500],
                fontSize: { xs: "1.25rem", sm: "1.3rem", md: "1.5rem" },
                mt: "auto",
              }}
            >
              {inStockCount.toLocaleString()}
            </Typography>
          )}
        </Box>

        <Box
          p={{ xs: "16px 20px", sm: "20px 24px" }}
          sx={{
            gridColumn: { md: "span 3", lg: "span 1" },
            backgroundColor:
              theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
            borderRadius: "12px",
            border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 4px 20px rgba(0,0,0,0.2)"
                : "0 4px 20px rgba(0,0,0,0.03)",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "10px",
          }}
        >
          <Box display="flex" alignItems="center" gap="10px">
            <Box sx={{ color: colors.redAccent[500] }}>
              <ReportOutlined sx={{ fontSize: "1.5rem" }} />
            </Box>
            <Typography
              variant="h6"
              color={colors.gray[400]}
              fontWeight="600"
              textTransform="uppercase"
              letterSpacing="1px"
              sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.875rem" } }}
            >
              Out-of-Stock
            </Typography>
          </Box>
          {isSummaryLoading ? (
            <Skeleton
              variant="rounded"
              width="60%"
              height={32}
              animation="wave"
              sx={{
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.06)",
                mt: "auto",
              }}
            />
          ) : (
            <Typography
              variant="h3"
              sx={{
                fontWeight: "800",
                color: colors.redAccent[500],
                fontSize: { xs: "1.25rem", sm: "1.3rem", md: "1.5rem" },
                mt: "auto",
              }}
            >
              {outOfStockCount.toLocaleString()}
            </Typography>
          )}
        </Box>

        <Box
          p={{ xs: "16px 20px", sm: "20px 24px" }}
          sx={{
            gridColumn: { sm: "span 2", md: "span 3", lg: "span 1" },
            backgroundColor:
              theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
            borderRadius: "12px",
            border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 4px 20px rgba(0,0,0,0.2)"
                : "0 4px 20px rgba(0,0,0,0.03)",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "10px",
          }}
        >
          <Box display="flex" alignItems="center" gap="10px">
            <Box sx={{ color: colors.yellowAccent[500] }}>
              <WarningAmberOutlinedIcon sx={{ fontSize: "1.5rem" }} />
            </Box>
            <Typography
              variant="h6"
              color={colors.gray[400]}
              fontWeight="600"
              textTransform="uppercase"
              letterSpacing="1px"
              sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.875rem" } }}
            >
              Low-Stock
            </Typography>
          </Box>
          {isSummaryLoading ? (
            <Skeleton
              variant="rounded"
              width="60%"
              height={32}
              animation="wave"
              sx={{
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.06)",
                mt: "auto",
              }}
            />
          ) : (
            <Typography
              variant="h3"
              sx={{
                fontWeight: "800",
                color: colors.yellowAccent[500],
                fontSize: { xs: "1.25rem", sm: "1.3rem", md: "1.5rem" },
                mt: "auto",
              }}
            >
              {lowStockCount.toLocaleString()}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Main Datagrid View */}
      <Box
        display="flex"
        flexDirection="column"
        sx={{
          opacity: 0,
          animation: "slideInUp 0.5s ease-out forwards",
          animationDelay: "0.3s",
        }}
      >
        <ProductForm
          open={open}
          handleClose={handleClose}
          onProductAdded={handleAddProduct}
          editProduct={editProduct}
          onProductEdited={handleProductEdited}
        />

        <Box
          height="70vh"
          sx={{
            backgroundColor:
              theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
            borderRadius: "12px",
            border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 4px 20px rgba(0,0,0,0.2)"
                : "0 4px 20px rgba(0,0,0,0.03)",
            overflow: "hidden",
            "& .MuiDataGrid-root": {
              border: "none",
              "--DataGrid-containerBackground":
                theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.02)"
                  : "rgba(0,0,0,0.01)",
              borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
              color: colors.gray[300],
              fontSize: "0.75rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "1px",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: "transparent",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.02)"
                  : "rgba(0,0,0,0.01)",
            },
            "& .MuiCheckbox-root": {
              color: `${colors.greenAccent[500]} !important`,
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${colors.gray[100]} !important`,
              margin: "0 !important",
              fontSize: "14px",
              [theme.breakpoints.down("sm")]: {
                minWidth: "36px !important",
                padding: "6px !important",
                fontSize: "0px !important",
                "& .MuiButton-startIcon": {
                  margin: "0 !important",
                },
                "& .MuiSvgIcon-root": {
                  fontSize: "1.25rem !important",
                },
              },
            },
            [theme.breakpoints.down("sm")]: {
              "& .MuiDataGrid-scrollbar": {
                display: "none",
              },
              "& .MuiDataGrid-virtualScroller, & .MuiDataGrid-main": {
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                "&::-webkit-scrollbar": { display: "none" },
              },
            },
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            getRowClassName={getRowClassName}
            loading={isLoading}
            slots={{
              toolbar: CustomToolbar,
              noRowsOverlay: CustomNoRowsOverlay,
              noResultsOverlay: CustomNoRowsOverlay,
              loadingOverlay: () => (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {[...Array(12)].map((_, index) => (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="center"
                      gap="16px"
                      sx={{
                        width: "100%",
                        height: "52px",
                        padding: "0 10px",
                        borderBottom: `1px solid ${
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.05)"
                            : "rgba(0,0,0,0.05)"
                        }`,
                      }}
                    >
                      {[
                        0.5, 0.5, 1, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 1,
                      ].map((flexValue, colIndex) => (
                        <Skeleton
                          key={colIndex}
                          variant="rounded"
                          animation="wave"
                          height={20}
                          sx={{
                            flex: flexValue,
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(0,0,0,0.06)",
                          }}
                        />
                      ))}
                    </Box>
                  ))}
                </Box>
              ),
            }}
            slotProps={{
              toolbar: {
                categoryFilter,
                setCategoryFilter,
                statusFilter,
                setStatusFilter,
                dateRangeFilter,
                setDateRangeFilter,
                searchQuery,
                setSearchQuery,
              },
              basePopper: {
                sx: {
                  "& .MuiPaper-root": {
                    animation: "popperFadeIn 0.3s ease-out",
                    "@keyframes popperFadeIn": {
                      "0%": { opacity: 0, transform: "translateY(-4px)" },
                      "100%": { opacity: 1, transform: "translateY(0)" },
                    },
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? `rgba(${parseInt(colors.primary[400].slice(1, 3), 16)}, ${parseInt(colors.primary[400].slice(3, 5), 16)}, ${parseInt(colors.primary[400].slice(5, 7), 16)}, 0.8)`
                        : "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(16px)",
                    border: `1px solid ${
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.15)"
                        : "rgba(0, 0, 0, 0.1)"
                    }`,
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 8px 32px 0 rgba(0, 0, 0, 0.4)"
                        : "0 8px 32px 0 rgba(0, 0, 0, 0.15)",
                    borderRadius: "12px",
                    mt: 1,
                    "& .MuiDataGrid-panelContent": { padding: "8px" },
                    "& .MuiDataGrid-panelFooter": {
                      borderTop: `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.1)"
                          : "rgba(0, 0, 0, 0.05)"
                      }`,
                      padding: "8px",
                    },
                    "& .MuiButton-root": {
                      textTransform: "none",
                      borderRadius: "8px",
                      fontWeight: 600,
                      padding: "6px 12px",
                      transition: "all 0.2s ease-in-out",
                    },
                    "& .MuiMenuItem-root, & .MuiListItem-root": {
                      padding: "12px 20px",
                      margin: "4px 8px",
                      gap: "12px",
                      borderRadius: "8px",
                      transition: "all 0.2s ease-in-out",
                      color: `${colors.gray[100]} !important`,
                      fontWeight: 600,
                      fontSize: "14px",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.1) !important"
                            : "rgba(0, 0, 0, 0.04) !important",
                      },
                      "&:active": { transform: "scale(0.95)" },
                      "&.Mui-selected": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.15)"
                            : "rgba(0, 0, 0, 0.08)",
                        fontWeight: "bold",
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.2)"
                            : "rgba(0, 0, 0, 0.12)",
                      },
                    },
                  },
                },
              },
              baseMenu: {
                TransitionComponent: Fade,
                transitionDuration: 300,
                PaperProps: {
                  sx: {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? `rgba(${parseInt(colors.primary[400].slice(1, 3), 16)}, ${parseInt(colors.primary[400].slice(3, 5), 16)}, ${parseInt(colors.primary[400].slice(5, 7), 16)}, 0.8)`
                        : "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(16px)",
                    border: `1px solid ${
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.15)"
                        : "rgba(0, 0, 0, 0.1)"
                    }`,
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 8px 32px 0 rgba(0, 0, 0, 0.4)"
                        : "0 8px 32px 0 rgba(0, 0, 0, 0.15)",
                    borderRadius: "12px",
                    mt: 1,
                    "& .MuiMenuItem-root": {
                      padding: "12px 20px",
                      margin: "4px 8px",
                      gap: "12px",
                      borderRadius: "8px",
                      transition: "all 0.2s ease-in-out",
                      color: `${colors.gray[100]} !important`,
                      fontWeight: 600,
                      fontSize: "14px",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.1) !important"
                            : "rgba(0, 0, 0, 0.04) !important",
                      },
                      "&:active": { transform: "scale(0.95)" },
                      "&.Mui-selected": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.15)"
                            : "rgba(0, 0, 0, 0.08)",
                        fontWeight: "bold",
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.2)"
                            : "rgba(0, 0, 0, 0.12)",
                      },
                    },
                  },
                },
              },
            }}
            initialState={{
              sorting: { sortModel: [{ field: "id", sort: "desc" }] },
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            pageSizeOptions={[25, 50, 100]}
          />
        </Box>

        <ProductDeleteDialog
          open={openDialog}
          onClose={handleCloseDialog}
          onConfirm={handleDeleteProduct}
          isBulkDelete={isBulkDelete}
        />

        <RestockModal
          open={restockOpen}
          handleClose={handleCloseRestock}
          productId={restockTarget?.id}
          product={restockTarget}
          onRestocked={handleOnRestocked}
        />

        <ProductHistoryModal
          open={historyOpen}
          handleClose={handleCloseHistory}
          product={historyTarget}
        />

        <SetThresholdModal
          open={thresholdOpen}
          handleClose={handleCloseThreshold}
          product={thresholdTarget}
          onThresholdUpdated={handleOnThresholdUpdated}
        />
      </Box>
    </Box>
  );
};

export default Inventory;
